import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { cancelStaffInvite, createStaffInvite, createStaffUser, getUsers, resendStaffInvite } from '../../services/api';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.75rem 1rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem' };

export default function Users() {
  const { projects, fetchProjects, updateProject } = useApp();
  const [staff, setStaff] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [inviteActionBusy, setInviteActionBusy] = useState('');
  const [lastInviteInfo, setLastInviteInfo] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());

  const load = async () => {
    await fetchProjects();
    const users = await getUsers({ role: 'staff' });
    setStaff(Array.isArray(users) ? users : []);
    if (!selectedUserId && Array.isArray(users) && users[0]?._id) setSelectedUserId(users[0]._id);
  };

  useEffect(() => {
    load().catch(() => setMessage('Failed to load users/projects'));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const assignedProjectIds = useMemo(() => {
    if (!selectedUserId) return [];
    return (projects || [])
      .filter((p) => (p.assignedUsers || []).some((u) => String(u._id || u) === String(selectedUserId)))
      .map((p) => String(p._id));
  }, [projects, selectedUserId]);

  const handleCreate = async () => {
    setMessage('');
    if (!email || !password) return setMessage('Email and password required');
    try {
      await createStaffUser({ email, password });
      setEmail('');
      setPassword('');
      setMessage('Staff user created');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleInvite = async () => {
    setMessage('');
    if (!inviteEmail) return setMessage('Invite email is required');
    try {
      const r = await createStaffInvite({ email: inviteEmail });
      setInviteEmail('');
      setMessage('Staff invite sent');
      setLastInviteInfo(r);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to send invite');
    }
  };

  const handleResendInvite = async (userId) => {
    setMessage('');
    setInviteActionBusy(String(userId));
    try {
      const r = await resendStaffInvite(userId);
      setMessage('Staff invite resent');
      setLastInviteInfo(r);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to resend invite');
    } finally {
      setInviteActionBusy('');
    }
  };

  const handleCancelInvite = async (userId) => {
    setMessage('');
    setInviteActionBusy(String(userId));
    try {
      await cancelStaffInvite(userId);
      setMessage('Staff invite canceled');
      await load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to cancel invite');
    } finally {
      setInviteActionBusy('');
    }
  };

  const copyInviteLink = async (inviteLink) => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setMessage('Invite link copied');
    } catch (err) {
      setMessage('Failed to copy invite link');
    }
  };

  const formatInviteCountdown = (inviteExpires) => {
    if (!inviteExpires) return 'No expiry';
    const remainingMs = new Date(inviteExpires).getTime() - nowTs;
    if (remainingMs <= 0) return 'Expired';
    const totalMinutes = Math.floor(remainingMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };

  const toggleAssignment = async (project, checked) => {
    if (!selectedUserId) return;
    setSaving(true);
    setMessage('');
    try {
      const current = (project.assignedUsers || []).map((u) => String(u._id || u));
      const nextSet = new Set(current);
      if (checked) nextSet.add(String(selectedUserId));
      else nextSet.delete(String(selectedUserId));
      await updateProject(project._id, {
        name: project.name,
        description: project.description || '',
        status: project.status || 'planning',
        progress: Number(project.progress || 0),
        startDate: project.startDate || null,
        dueDate: project.dueDate || null,
        completedDate: project.completedDate || null,
        notes: project.notes || '',
        assignedUsers: Array.from(nextSet),
      });
      await fetchProjects();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to update assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Users</h1>

      <div style={{ ...card, padding: '1rem', marginBottom: '1rem' }}>
        <h2 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>Create Staff User</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem' }}
        >
          <input
            style={input}
            placeholder="staff@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <input
            style={input}
            placeholder="temporary password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <button type="submit" style={btn}>Create</button>
        </form>
      </div>

      <div style={{ ...card, padding: '1rem', marginBottom: '1rem' }}>
        <h2 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>Invite Staff by Email</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleInvite();
          }}
          style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem' }}
        >
          <input
            style={input}
            placeholder="staff@company.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            autoComplete="email"
          />
          <button type="submit" style={btn}>Send Invite</button>
        </form>
      </div>

      {lastInviteInfo?.inviteLink && (
        <div style={{ ...card, padding: '1rem', marginBottom: '1rem', border: '1px solid rgba(16,185,129,0.35)' }}>
          <h2 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>Latest Invite Link</h2>
          <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
            For fallback sharing if email delivery is delayed.
          </div>
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', color: '#cbd5e1', background: 'rgba(15,23,42,0.55)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0.55rem', padding: '0.6rem' }}>
              {lastInviteInfo.inviteLink}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button style={{ ...btn, padding: '0.5rem 0.75rem' }} onClick={() => copyInviteLink(lastInviteInfo.inviteLink)}>Copy Link</button>
              <button style={{ ...btn, padding: '0.5rem 0.75rem' }} onClick={() => window.open(lastInviteInfo.inviteLink, '_blank')}>Open Link</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1rem' }}>
        <div style={{ ...card, padding: '1rem' }}>
          <h2 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>Staff Users</h2>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {staff.map((u) => (
              <div
                key={u._id}
                onClick={() => setSelectedUserId(u._id)}
                style={{
                  textAlign: 'left',
                  padding: '0.6rem 0.75rem',
                  borderRadius: '0.6rem',
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: String(selectedUserId) === String(u._id) ? 'rgba(16,185,129,0.16)' : 'rgba(148,163,184,0.07)',
                  color: '#e2e8f0',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: u.invitePending ? '0.45rem' : 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '13rem' }}>{u.email}</span>
                  {u.invitePending ? (
                    <span style={{ marginLeft: 'auto', padding: '0.1rem 0.45rem', borderRadius: '999px', fontSize: '0.7rem', background: 'rgba(251,191,36,0.2)', color: '#fcd34d' }}>Invited</span>
                  ) : (
                    <span style={{ marginLeft: 'auto', padding: '0.1rem 0.45rem', borderRadius: '999px', fontSize: '0.7rem', background: 'rgba(16,185,129,0.2)', color: '#34d399' }}>Active</span>
                  )}
                </div>
                {u.invitePending && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.75rem', color: '#fcd34d' }}>{formatInviteCountdown(u.inviteExpires)}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleResendInvite(u._id); }}
                      disabled={inviteActionBusy === String(u._id)}
                      style={{ padding: '0.22rem 0.48rem', borderRadius: '0.45rem', fontSize: '0.72rem', background: 'rgba(16,185,129,0.22)', color: '#34d399' }}
                    >
                      Resend
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); copyInviteLink(u.inviteLink); }}
                      style={{ padding: '0.22rem 0.48rem', borderRadius: '0.45rem', fontSize: '0.72rem', background: 'rgba(56,189,248,0.2)', color: '#7dd3fc' }}
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCancelInvite(u._id); }}
                      disabled={inviteActionBusy === String(u._id)}
                      style={{ padding: '0.22rem 0.48rem', borderRadius: '0.45rem', fontSize: '0.72rem', background: 'rgba(239,68,68,0.2)', color: '#fca5a5' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
            {!staff.length && <div style={{ color: '#94a3b8' }}>No staff users yet</div>}
          </div>
        </div>

        <div style={{ ...card, padding: '1rem' }}>
          <h2 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>Project Assignments</h2>
          {!selectedUserId ? (
            <div style={{ color: '#94a3b8' }}>Select a user to assign projects</div>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {(projects || []).map((p) => {
                const checked = assignedProjectIds.includes(String(p._id));
                return (
                  <label key={p._id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem', borderRadius: '0.5rem', background: 'rgba(148,163,184,0.06)' }}>
                    <input type="checkbox" checked={checked} onChange={(e) => toggleAssignment(p, e.target.checked)} disabled={saving} />
                    <span>{p.name}</span>
                    <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '0.85rem' }}>{p.clientName || ''}</span>
                  </label>
                );
              })}
              {!projects.length && <div style={{ color: '#94a3b8' }}>No projects available</div>}
            </div>
          )}
        </div>
      </div>

      {message && <div style={{ marginTop: '0.75rem', color: message.toLowerCase().includes('failed') ? '#f87171' : '#34d399' }}>{message}</div>}
    </div>
  );
}
